import { useCallback, useEffect, useMemo } from 'react';
import { MenuButton } from './MenuButton';
import { StatusIcon } from './StatusIcon';
import { getUserHangupSubStatus } from 'services/api/admin';
import { OnlineStatus } from 'services/api/assistant.account';
import { useMemoizedFn, useRequest } from 'ahooks';
import { Modal, Typography, message } from 'antd';
import { sendSystem } from 'services/nim';
import useNotice from 'hooks/useNotice';
import useModal from 'hooks/useModal';
import { RightOutlined } from '@ant-design/icons';
import { HangupChange } from './HangupChange';
import { useTranslate } from '@tolgee/react';
import { useAuthorization } from 'components/Authorization';

interface HangupMenuButtonProps {
  popoverVisible: boolean;
  currentOnlineStatus: OnlineStatus;
  hidePopover: () => void;
  onCurrentOnlineStatus: (value: OnlineStatus) => void;
}

const statusActionMap = {
  ONLINE: 'RECOVER',
  HANGUP: 'HANG',
};

const updateStatus = (
  id: number,
  status: Exclude<Conversation.Status, 'OFFLINE'>,
  subStatus = '',
) => {
  return sendSystem({
    scene: 'p2p',
    to: 'leyan',
    sendToOnlineUsersOnly: false,
    cc: true,
    content: JSON.stringify({
      id,
      action: statusActionMap[status],
      subStatus,
    }),
  });
};

const MAX_VALUES = 5;

export const HangupMenuButton = (props: HangupMenuButtonProps) => {
  const { currentOnlineStatus, popoverVisible, hidePopover, onCurrentOnlineStatus } = props;
  const { t } = useTranslate();
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const { visible, show, hide } = useModal();
  const { visible: hangupConfirmVisible, update: updateHangupConfirm } = useNotice(
    'user/status/hangup',
    true,
  );

  const { status, subStatus } = currentOnlineStatus;

  const { data: hangupSubStatus, runAsync: getHangupSubStatus } = useRequest(
    () => getUserHangupSubStatus(),
    {
      manual: true,
    },
  );

  // 切换挂起子状态
  const changeHangupSubStatus = useMemoizedFn((subStatus: string) => {
    hidePopover();
    if (
      (status === 'HANGUP' && subStatus === currentOnlineStatus.subStatus) ||
      payload?.id === undefined
    ) {
      return;
    }
    if (status === 'OFFLINE') {
      message.warn(t('operation.offline'));
      return;
    }

    const hangupAction = () => {
      if (payload?.id) {
        updateStatus(payload.id, 'HANGUP', subStatus)
          .then(() => {
            onCurrentOnlineStatus({
              status: 'HANGUP',
              subStatus,
            });
            message.success(t('hangup.succeed'));
          })
          .catch(() => {
            message.success(t('hangup.failed'));
          });
      }
    };

    if (!hangupConfirmVisible) {
      hangupAction();
      return;
    }

    Modal.confirm({
      title: t('confirm.title'),
      content: t('confirm.content'),
      onOk: () => {
        updateHangupConfirm(false);
        hangupAction();
      },
      onCancel: () => {
        updateHangupConfirm(false);
      },
    });
  });

  const openHangupChange = useCallback(() => {
    hidePopover();
    show();
  }, [hidePopover, show]);

  const handleConfirm = useCallback(
    (value: string) => {
      changeHangupSubStatus(value);
      hide();
    },
    [changeHangupSubStatus, hide],
  );

  // 只保留 5 个子状态选择
  const subStatusButton = useMemo(() => {
    const renderer = hangupSubStatus?.slice(0, MAX_VALUES) ?? [];
    if (currentOnlineStatus.subStatus && !renderer.includes(currentOnlineStatus.subStatus)) {
      renderer.unshift(currentOnlineStatus.subStatus);
    }
    return renderer.map((subStatus) => (
      <MenuButton
        key={subStatus}
        label={subStatus}
        onClick={() => changeHangupSubStatus(subStatus)}
        active={subStatus === currentOnlineStatus.subStatus}
      />
    ));
  }, [hangupSubStatus, currentOnlineStatus.subStatus, changeHangupSubStatus]);

  // 无子状态时，挂起状态可以触发
  const canHangupTrigger = useMemo(
    () => !hangupSubStatus || hangupSubStatus?.length === 0,
    [hangupSubStatus],
  );

  const showOtherTrigger = useMemo(
    () => hangupSubStatus && hangupSubStatus?.length > MAX_VALUES,
    [hangupSubStatus],
  );

  const handleHangupClick = useCallback(() => {
    if (!canHangupTrigger) return;

    changeHangupSubStatus('');
  }, [canHangupTrigger, changeHangupSubStatus]);

  useEffect(() => {
    if (popoverVisible) {
      getHangupSubStatus();
    }
  }, [popoverVisible, getHangupSubStatus]);

  return (
    <>
      <MenuButton
        key="HANGUP"
        label={t('status.hangup')}
        customIcon={<StatusIcon status="HANGUP" />}
        hoverHighlight={canHangupTrigger}
        active={status === 'HANGUP' && !subStatus}
        onClick={handleHangupClick}
      />
      {!canHangupTrigger && (
        <div className="pl-5">
          {subStatusButton}
          {showOtherTrigger && (
            <MenuButton
              label={
                <div className="flex items-center gap-1">
                  <Typography.Paragraph
                    className="max-w-[106px] !mb-0"
                    ellipsis={{ tooltip: true }}
                  >
                    {t('hangup_select_others_btn')}
                  </Typography.Paragraph>
                  <RightOutlined />
                </div>
              }
              onClick={openHangupChange}
            />
          )}
        </div>
      )}

      {visible && (
        <HangupChange
          hangupStatus={currentOnlineStatus.subStatus}
          hangupStatusList={hangupSubStatus}
          visible={visible}
          onCancel={hide}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
